import * as THREE from 'three'
import {
  type CompiledClipState,
  type CompiledClipVolume,
  CompiledClipVolumeKind,
  MAX_COMPILED_CLIP_VOLUMES,
} from '@/shared/clipping/CompiledClipState'
import { ClipPolygonMaxVerticeCount } from '@/shared/constants'

/** 每个 volume 行头：mat4(4) + bounds(1) + metadata(1)。 */
export const CLIP_STATE_HEADER_TEXELS = 6

/** 每个 volume 在纹理中的固定行宽。 */
export const CLIP_STATE_TEXTURE_WIDTH = CLIP_STATE_HEADER_TEXELS + ClipPolygonMaxVerticeCount

function getWorldToClipMatrix(volume: CompiledClipVolume): THREE.Matrix4 {
  return volume.kind === CompiledClipVolumeKind.POLYGON
    ? volume.worldViewProjection
    : volume.worldToBox
}

function writeMatrix(target: Float32Array, offset: number, matrix: THREE.Matrix4): void {
  for (let index = 0; index < matrix.elements.length; index += 1) {
    target[offset + index] = matrix.elements[index]!
  }
}

function createTexture(data: Float32Array): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    data,
    CLIP_STATE_TEXTURE_WIDTH,
    MAX_COMPILED_CLIP_VOLUMES,
    THREE.RGBAFormat,
    THREE.FloatType
  )
  texture.name = 'ClipStateData'
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.colorSpace = THREE.NoColorSpace
  texture.needsUpdate = true
  return texture
}

/** 可原地更新的资产无关裁切 GPU 数据包。 */
export class ClipStatePacket {
  readonly texture: THREE.DataTexture
  volumeCount = 0
  maxPolygonVertices = 0
  sourceVersion = 0

  private readonly data: Float32Array
  private disposed = false

  constructor(state: CompiledClipState) {
    this.data = new Float32Array(CLIP_STATE_TEXTURE_WIDTH * MAX_COMPILED_CLIP_VOLUMES * 4)
    this.texture = createTexture(this.data)
    this.update(state)
  }

  /** 使用最新状态原地覆盖 packet，不重新分配纹理。 */
  update(state: CompiledClipState): void {
    if (this.disposed) return
    this.data.fill(0)

    for (let volumeIndex = 0; volumeIndex < state.volumes.length; volumeIndex += 1) {
      const volume = state.volumes[volumeIndex]!
      const rowOffset = volumeIndex * CLIP_STATE_TEXTURE_WIDTH * 4
      const boundsOffset = rowOffset + 4 * 4
      const metadataOffset = rowOffset + 5 * 4
      writeMatrix(this.data, rowOffset, getWorldToClipMatrix(volume))

      if (volume.kind === CompiledClipVolumeKind.POLYGON) {
        this.data[boundsOffset] = volume.aabb.x
        this.data[boundsOffset + 1] = volume.aabb.y
        this.data[boundsOffset + 2] = volume.aabb.z
        this.data[boundsOffset + 3] = volume.aabb.w
        this.data[metadataOffset] = volume.vertices.length
        for (let vertexIndex = 0; vertexIndex < volume.vertices.length; vertexIndex += 1) {
          const vertex = volume.vertices[vertexIndex]!
          const vertexOffset = rowOffset + (CLIP_STATE_HEADER_TEXELS + vertexIndex) * 4
          this.data[vertexOffset] = vertex.x
          this.data[vertexOffset + 1] = vertex.y
        }
      } else {
        this.data[boundsOffset] = -0.5
        this.data[boundsOffset + 1] = -0.5
        this.data[boundsOffset + 2] = 0.5
        this.data[boundsOffset + 3] = 0.5
      }

      this.data[metadataOffset + 1] = volume.clipTask
      this.data[metadataOffset + 2] = volume.groupIndex
      this.data[metadataOffset + 3] = volume.kind
    }

    this.volumeCount = state.volumes.length
    this.maxPolygonVertices = state.polygons.reduce(
      (maximum, polygon) => Math.max(maximum, polygon.vertices.length),
      0
    )
    this.sourceVersion = state.version
    this.texture.needsUpdate = true
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.volumeCount = 0
    this.texture.dispose()
  }
}
